import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Music, Trophy, Laugh, Drama, PartyPopper, Building2, 
  FerrisWheel, Mountain, Users, Moon, Search, Calendar, MapPin,
  ArrowRight, Loader2, ChevronRight, Star, Heart, Clock,
  Ticket, Globe, Sparkles, Play, Mic, MicOff, Send, X, Bot, CloudSun
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FloatingBackButton } from '../components/layout/FloatingBackButton';
import WeatherWidget from '../components/weather/WeatherWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Speech Recognition
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const hasSpeechRecognition = !!SpeechRecognition;

// Category icons and colors
const CATEGORIES = [
  { id: 'all', name: 'All', icon: Globe, color: '#00E5FF' },
  { id: 'concerts', name: 'Concerts', icon: Music, color: '#FF007F', emoji: '🎵' },
  { id: 'sports', name: 'Sports', icon: Trophy, color: '#FFD700', emoji: '🏟' },
  { id: 'comedy', name: 'Comedy', icon: Laugh, color: '#FF6B35', emoji: '😂' },
  { id: 'theatre', name: 'Theatre', icon: Drama, color: '#9B59B6', emoji: '🎭' },
  { id: 'festivals', name: 'Festivals', icon: PartyPopper, color: '#E74C3C', emoji: '🎪' },
  { id: 'museums', name: 'Museums', icon: Building2, color: '#3498DB', emoji: '🏛' },
  { id: 'attractions', name: 'Attractions', icon: FerrisWheel, color: '#2ECC71', emoji: '🎡' },
  { id: 'adventure', name: 'Adventure', icon: Mountain, color: '#1ABC9C', emoji: '🚁' },
  { id: 'family', name: 'Family', icon: Users, color: '#F39C12', emoji: '👨‍👩‍👧' },
  { id: 'nightlife', name: 'Nightlife', icon: Moon, color: '#8E44AD', emoji: '🌃' },
];

// Destination imagery
const DESTINATION_IMAGES = {
  'paris': {
    hero: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1920&q=80',
    tagline: 'The City of Light Awaits',
    description: 'Experience world-class concerts, legendary theatre, and unforgettable cultural moments.'
  },
  'dubai': {
    hero: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80',
    tagline: 'Where Dreams Touch the Sky',
    description: 'From spectacular live shows to desert adventures, discover experiences as extraordinary as the city itself.'
  },
  'new york': {
    hero: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=1920&q=80',
    tagline: 'The City That Never Sleeps',
    description: 'Broadway, world-famous concerts, legendary sports - experience the energy that defines New York.'
  },
  'london': {
    hero: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1920&q=80',
    tagline: 'Where History Meets Modern Magic',
    description: 'West End shows, royal experiences, and iconic venues await in this timeless metropolis.'
  },
  'tokyo': {
    hero: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1920&q=80',
    tagline: 'Where Tradition Meets Tomorrow',
    description: 'From ancient temples to cutting-edge entertainment, Tokyo offers experiences unlike anywhere else.'
  },
  'las vegas': {
    hero: 'https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=1920&q=80',
    tagline: 'Entertainment Capital of the World',
    description: 'World-class shows, legendary concerts, and non-stop entertainment.'
  },
  'los angeles': {
    hero: 'https://images.unsplash.com/photo-1534190760961-74e8c1c5c3da?w=1920&q=80',
    tagline: 'Where Stars Are Born',
    description: 'From Hollywood premieres to beach concerts, experience the magic of LA.'
  },
  'default': {
    hero: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=1920&q=80',
    tagline: 'Discover Extraordinary Experiences',
    description: 'Find concerts, shows, sports, and unique experiences in destinations around the world.'
  }
};

// Popular destinations
const POPULAR_DESTINATIONS = [
  { name: 'New York', code: 'NYC', country: 'USA' },
  { name: 'London', code: 'LON', country: 'UK' },
  { name: 'Paris', code: 'PAR', country: 'France' },
  { name: 'Las Vegas', code: 'LAS', country: 'USA' },
  { name: 'Dubai', code: 'DXB', country: 'UAE' },
  { name: 'Los Angeles', code: 'LAX', country: 'USA' },
  { name: 'Tokyo', code: 'TYO', country: 'Japan' },
  { name: 'Miami', code: 'MIA', country: 'USA' },
  { name: 'Chicago', code: 'CHI', country: 'USA' },
  { name: 'Sydney', code: 'SYD', country: 'Australia' },
];

// Section Header Component
const SectionHeader = ({ eyebrow, title, subtitle, accentColor = '#00E5FF', align = 'left' }) => (
  <div className={`mb-10 ${align === 'center' ? 'text-center' : ''}`}>
    <p className="text-sm uppercase tracking-[0.3em] mb-3 font-medium" style={{ color: accentColor }}>
      {eyebrow}
    </p>
    <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-white font-bold mb-3">
      {title}
    </h2>
    {subtitle && <p className="text-white/50 max-w-2xl">{subtitle}</p>}
  </div>
);

// Event Card Component
const EventCard = ({ event, size = 'normal' }) => {
  const sizeClasses = {
    large: 'md:col-span-2 md:row-span-2 min-h-[400px]',
    normal: 'min-h-[280px]',
    wide: 'md:col-span-2 min-h-[200px]'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className={`relative overflow-hidden rounded-2xl cursor-pointer group ${sizeClasses[size]}`}
      data-testid={`event-card-${event.id}`}
    >
      <img 
        src={event.image || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=800&q=80'}
        alt={event.name}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
      
      {/* Category Badge */}
      <div className="absolute top-4 left-4">
        <span className="bg-[#FF007F] text-white text-xs uppercase tracking-wider px-3 py-1 rounded-full shadow-[0_0_15px_rgba(255,0,127,0.5)]">
          {event.category}
        </span>
      </div>

      {/* Status Badge */}
      {event.status && event.status !== 'onsale' && (
        <div className="absolute top-4 right-4">
          <span className="bg-amber-500 text-black text-xs uppercase tracking-wider px-3 py-1 rounded-full font-medium">
            {event.status === 'offsale' ? 'Sold Out' : event.status}
          </span>
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-5">
        {/* Date & Time */}
        <div className="flex items-center gap-3 text-white/60 text-sm mb-2">
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(event.date)}
          </span>
          {event.time && (
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatTime(event.time)}
            </span>
          )}
        </div>
        
        {/* Event Name */}
        <h3 className={`font-['Playfair_Display'] text-white font-bold mb-2 line-clamp-2 ${size === 'large' ? 'text-2xl' : 'text-xl'}`}>
          {event.name}
        </h3>
        
        {/* Venue */}
        {event.venue && (
          <p className="text-white/60 text-sm flex items-center gap-1 mb-3">
            <MapPin className="w-4 h-4" />
            {event.venue.name}{event.venue.city ? `, ${event.venue.city}` : ''}
          </p>
        )}
        
        {/* Price & Book Button */}
        <div className="flex items-center justify-between">
          {event.price ? (
            <div>
              <p className="text-[#00E5FF] text-lg font-bold">
                {event.price.currency} {event.price.min?.toLocaleString()}
                {event.price.max && event.price.max !== event.price.min && `+`}
              </p>
            </div>
          ) : (
            <p className="text-white/40 text-sm">Price TBA</p>
          )}
          <a 
            href={event.url} 
            target="_blank" 
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white text-sm font-semibold px-4 py-2 rounded-xl hover:shadow-[0_0_20px_rgba(255,0,127,0.4)] transition-all flex items-center gap-2"
          >
            <Ticket className="w-4 h-4" />
            Book Now
          </a>
        </div>
      </div>
    </motion.div>
  );
};

// Experience Concierge Component
const ExperienceConcierge = ({ onSearch, currentCity }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId] = useState(() => `exp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (hasSpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputText(transcript);
        
        if (event.results[0].isFinal) {
          setIsListening(false);
          setTimeout(() => handleSendMessage(transcript), 500);
        }
      };

      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }

    return () => recognitionRef.current?.abort();
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      fetchGreeting();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchGreeting = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/experiences/concierge/greeting`);
      if (response.data.success) {
        setMessages([{
          id: Date.now(),
          role: 'assistant',
          content: response.data.greeting,
          type: 'greeting'
        }]);
      }
    } catch (error) {
      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: "Hello! I'm your AIRA Experience Concierge. What kind of experiences are you looking for?",
        type: 'greeting'
      }]);
    }
  };

  const toggleListening = () => {
    if (!hasSpeechRecognition) {
      toast.error('Voice recognition not supported. Please use Chrome or Edge.');
      return;
    }
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      setInputText('');
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  const handleSendMessage = useCallback(async (text = inputText) => {
    if (!text.trim() || isProcessing) return;

    const userMessage = { id: Date.now(), role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);

    try {
      const response = await axios.post(`${API_URL}/api/experiences/concierge/chat`, {
        message: text.trim(),
        session_id: sessionId,
        context: { current_city: currentCity }
      });

      if (response.data.success) {
        const data = response.data.data;
        
        setMessages(prev => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.concierge_response || 'I understand your request.',
          type: data.type,
          data: data
        }]);

        // Trigger search if we have enough data
        if (data.type === 'event_search' && data.confidence >= 0.7) {
          onSearch({
            city: data.city,
            category: data.category,
            keyword: data.keyword,
            start_date: data.start_date,
            end_date: data.end_date
          });
          toast.success('Searching for experiences!');
          setTimeout(() => setIsOpen(false), 1500);
        } else if (data.type === 'destination' && data.city) {
          onSearch({ city: data.city });
          toast.success(`Exploring ${data.city}!`);
          setTimeout(() => setIsOpen(false), 1500);
        }
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'I apologize, but I encountered an issue. Please try again.',
        type: 'error'
      }]);
    } finally {
      setIsProcessing(false);
    }
  }, [inputText, isProcessing, sessionId, currentCity, onSearch]);

  const quickActions = [
    { icon: Music, label: 'Find Concerts', prompt: 'Show me upcoming concerts' },
    { icon: Trophy, label: 'Sports Events', prompt: 'Find sports events near me' },
    { icon: Drama, label: 'Theatre Shows', prompt: 'Find theatre and Broadway shows' },
    { icon: Sparkles, label: 'Unique Experiences', prompt: 'Recommend unique experiences' },
  ];

  return (
    <>
      {/* Floating Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-r from-[#FF007F] via-[#7B2FBE] to-[#00E5FF] shadow-[0_0_40px_rgba(255,0,127,0.5)] flex items-center justify-center group"
        data-testid="experience-concierge-btn"
      >
        <div className="absolute inset-0.5 rounded-full bg-[#12101A] flex items-center justify-center">
          <Bot className="w-7 h-7 text-white group-hover:text-[#00E5FF] transition-colors" />
        </div>
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00E5FF] animate-ping opacity-20" />
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0D0B14] border-l border-white/10 z-50 flex flex-col overflow-hidden"
              data-testid="experience-concierge-panel"
            >
              {/* Header */}
              <div className="relative p-6 border-b border-white/10 bg-gradient-to-b from-[#1a1625] to-transparent">
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white/70" />
                </button>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#00E5FF] p-0.5">
                    <div className="w-full h-full rounded-2xl bg-[#12101A] flex items-center justify-center">
                      <Sparkles className="w-7 h-7 text-[#FF007F]" />
                    </div>
                  </div>
                  <div>
                    <h2 className="font-['Playfair_Display'] text-xl text-white font-bold">Experience Concierge</h2>
                    <p className="text-white/50 text-sm">Your guide to unforgettable moments</p>
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-xs uppercase tracking-wider">Available 24/7</span>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl p-4 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-[#FF007F] to-[#7B2FBE] text-white'
                          : 'bg-white/5 border border-white/10 text-white/90'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-[#FF007F]" />
                          <span className="text-[#FF007F] text-xs uppercase tracking-wider">Concierge</span>
                        </div>
                      )}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      
                      {/* Recommendations */}
                      {message.data?.recommendations && (
                        <div className="mt-4 space-y-2">
                          {message.data.recommendations.map((rec, idx) => (
                            <div key={idx} className="bg-white/5 rounded-xl p-3 border border-white/10">
                              <h4 className="font-semibold text-white">{rec.experience}</h4>
                              <p className="text-white/60 text-xs mt-1">{rec.why}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Search Ready Indicator */}
                      {message.data?.type === 'event_search' && message.data?.confidence >= 0.7 && (
                        <div className="mt-3 p-3 bg-black/20 rounded-xl">
                          <div className="flex items-center gap-2 text-xs text-white/60 mb-2">
                            <Search className="w-4 h-4" />
                            <span>Experience Search Ready</span>
                          </div>
                          <div className="text-sm space-y-1">
                            {message.data.city && <p><span className="text-white/50">City:</span> {message.data.city}</p>}
                            {message.data.category && <p><span className="text-white/50">Category:</span> {message.data.category}</p>}
                            {message.data.start_date && <p><span className="text-white/50">Date:</span> {message.data.start_date}</p>}
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {isProcessing && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 text-[#FF007F] animate-spin" />
                        <span className="text-white/60 text-sm">Finding experiences...</span>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Quick Actions */}
              {messages.length <= 1 && (
                <div className="px-4 py-3 border-t border-white/5">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSendMessage(action.prompt)}
                        className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-left group"
                      >
                        <action.icon className="w-4 h-4 text-[#FF007F] group-hover:text-[#00E5FF] transition-colors" />
                        <span className="text-white/70 text-sm group-hover:text-white transition-colors">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t border-white/10 bg-gradient-to-t from-[#0D0B14] to-transparent">
                {isListening && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-3 p-3 rounded-xl bg-[#FF007F]/10 border border-[#FF007F]/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-[#FF007F] animate-pulse" />
                      <span className="text-[#FF007F] text-sm">Listening...</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-2">
                      {[...Array(12)].map((_, i) => (
                        <motion.div
                          key={i}
                          animate={{ height: [4, Math.random() * 24 + 4, 4] }}
                          transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.05 }}
                          className="w-1 bg-[#FF007F] rounded-full"
                        />
                      ))}
                    </div>
                  </motion.div>
                )}

                <div className="flex items-center gap-2">
                  <button
                    onClick={toggleListening}
                    disabled={isProcessing}
                    className={`p-3 rounded-xl transition-all ${
                      isListening
                        ? 'bg-[#FF007F] text-white shadow-[0_0_20px_rgba(255,0,127,0.5)]'
                        : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                    } disabled:opacity-50`}
                    data-testid="voice-btn"
                  >
                    {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>

                  <input
                    type="text"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                    placeholder="What experiences are you looking for?"
                    disabled={isProcessing || isListening}
                    className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] transition-all disabled:opacity-50"
                    data-testid="concierge-input"
                  />

                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputText.trim() || isProcessing}
                    className="p-3 rounded-xl bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white hover:shadow-[0_0_20px_rgba(255,0,127,0.4)] transition-all disabled:opacity-50"
                    data-testid="send-btn"
                  >
                    {isProcessing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </div>

                {hasSpeechRecognition && (
                  <p className="text-center text-white/30 text-xs mt-3">
                    Tap the microphone to speak your request
                  </p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Main Experiences Page Component
export default function ExperiencesPage() {
  const { token } = useAuth();
  const [searchCity, setSearchCity] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [heroImage, setHeroImage] = useState(DESTINATION_IMAGES.default.hero);
  const [heroTagline, setHeroTagline] = useState(DESTINATION_IMAGES.default.tagline);
  const [heroDescription, setHeroDescription] = useState(DESTINATION_IMAGES.default.description);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [trendingEvents, setTrendingEvents] = useState([]);
  const [weekendEvents, setWeekendEvents] = useState([]);

  // Update hero based on city input
  useEffect(() => {
    const cityLower = searchCity.toLowerCase();
    const matched = Object.keys(DESTINATION_IMAGES).find(key => cityLower.includes(key));
    if (matched && matched !== 'default') {
      const dest = DESTINATION_IMAGES[matched];
      setHeroImage(dest.hero);
      setHeroTagline(dest.tagline);
      setHeroDescription(dest.description);
    } else if (!searchCity) {
      setHeroImage(DESTINATION_IMAGES.default.hero);
      setHeroTagline(DESTINATION_IMAGES.default.tagline);
      setHeroDescription(DESTINATION_IMAGES.default.description);
    }
  }, [searchCity]);

  // Fetch initial trending events
  useEffect(() => {
    fetchTrendingEvents();
    fetchWeekendEvents();
  }, []);

  const fetchTrendingEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/experiences/trending?limit=8`);
      if (response.data.success) {
        setTrendingEvents(response.data.events);
      }
    } catch (error) {
      console.error('Failed to fetch trending events:', error);
    }
  };

  const fetchWeekendEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/experiences/weekend?limit=6`);
      if (response.data.success) {
        setWeekendEvents(response.data.events);
      }
    } catch (error) {
      console.error('Failed to fetch weekend events:', error);
    }
  };

  const searchEvents = async (params = {}) => {
    const city = params.city || selectedCity || searchCity;
    const category = params.category || (selectedCategory !== 'all' ? selectedCategory : null);
    
    if (!city && !category && !params.keyword) {
      toast.error('Please enter a destination or select a category');
      return;
    }

    setLoading(true);
    setSearched(true);
    setEvents([]);

    try {
      const response = await axios.post(`${API_URL}/api/experiences/search`, {
        city: city || null,
        category: category,
        keyword: params.keyword || null,
        start_date: params.start_date || null,
        end_date: params.end_date || null,
        size: 24
      });

      if (response.data.success) {
        setEvents(response.data.events);
        if (response.data.events.length > 0) {
          toast.success(`Found ${response.data.events.length} experiences!`);
        } else {
          toast.info('No experiences found. Try a different search.');
        }
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search experiences');
    } finally {
      setLoading(false);
    }
  };

  const handleConciergeSearch = (params) => {
    if (params.city) {
      setSearchCity(params.city);
      setSelectedCity(params.city);
    }
    if (params.category) {
      setSelectedCategory(params.category);
    }
    searchEvents(params);
  };

  const filterDestinations = (query) => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    return POPULAR_DESTINATIONS.filter(
      d => d.name.toLowerCase().includes(q) || d.code.toLowerCase().includes(q)
    ).slice(0, 6);
  };

  return (
    <div className="min-h-screen bg-[#09080E]" data-testid="experiences-page">
      {/* Floating Back Button */}
      <FloatingBackButton />

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative h-[90vh] min-h-[700px] overflow-hidden">
        {/* Dynamic Background */}
        <motion.div 
          key={heroImage}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: 'easeOut' }}
          className="absolute inset-0"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#09080E]/70 via-transparent to-[#09080E]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09080E]/60 via-transparent to-[#09080E]/60" />
        </motion.div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col justify-end pb-12 px-4 md:px-16">
          {/* Editorial Title */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-10"
          >
            <p className="text-[#FF007F] text-sm uppercase tracking-[0.3em] mb-4">
              AIRA Experiences
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl lg:text-7xl text-white font-bold leading-tight max-w-4xl">
              {heroTagline.split(' ').slice(0, -1).join(' ')} <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF007F] via-[#00E5FF] to-[#FF007F]">
                {heroTagline.split(' ').slice(-1)}
              </span>
            </h1>
            <p className="text-white/60 text-lg mt-4 max-w-xl">
              {heroDescription}
            </p>
          </motion.div>

          {/* Glass Search Widget */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-black/40 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl max-w-4xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Destination */}
              <div className="relative md:col-span-1">
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Destination</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FF007F]" />
                  <input
                    type="text"
                    value={searchCity}
                    onChange={(e) => {
                      setSearchCity(e.target.value);
                      setShowSuggestions(true);
                    }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="Where to?"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder-white/30 focus:border-[#FF007F] focus:ring-1 focus:ring-[#FF007F] transition-all"
                    data-testid="destination-input"
                  />
                  {showSuggestions && filterDestinations(searchCity).length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-[#12101A] border border-white/10 rounded-xl overflow-hidden z-50 shadow-xl">
                      {filterDestinations(searchCity).map((dest) => (
                        <button
                          key={dest.code}
                          onClick={() => {
                            setSearchCity(dest.name);
                            setSelectedCity(dest.name);
                            setShowSuggestions(false);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                        >
                          <span className="text-white font-medium">{dest.name}</span>
                          <span className="text-white/50 ml-2 text-sm">{dest.country}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Category */}
              <div className="md:col-span-1">
                <label className="text-white/50 text-xs uppercase tracking-wider mb-1 block">Category</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <select 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)} 
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white appearance-none cursor-pointer"
                    data-testid="category-select"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id} className="bg-[#12101A]">
                        {cat.emoji ? `${cat.emoji} ` : ''}{cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Search Button */}
              <div className="md:col-span-1 flex items-end">
                <button 
                  onClick={() => searchEvents()} 
                  disabled={loading} 
                  className="w-full bg-gradient-to-r from-[#FF007F] to-[#FF4081] text-white font-semibold py-3 px-6 rounded-xl hover:shadow-[0_0_30px_rgba(255,0,127,0.4)] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="search-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                  Discover
                </button>
              </div>
            </div>

            {/* Quick Destinations */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-white/40 text-sm mb-3">Popular destinations</p>
              <div className="flex flex-wrap gap-2">
                {['New York', 'London', 'Paris', 'Las Vegas', 'Dubai', 'Tokyo'].map(city => (
                  <button
                    key={city}
                    onClick={() => {
                      setSearchCity(city);
                      setSelectedCity(city);
                    }}
                    className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-white/70 hover:bg-white/10 hover:border-white/20 hover:text-white transition-all text-sm"
                  >
                    {city}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Weather Widget for Selected Destination */}
            {(searchCity || selectedCity) && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <CloudSun className="w-4 h-4 text-[#FFD700]" />
                  <p className="text-white/40 text-sm">Weather in {searchCity || selectedCity}</p>
                </div>
                <WeatherWidget destination={searchCity || selectedCity} showForecast={true} />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ==================== CATEGORY TABS ==================== */}
      <section className="px-4 md:px-16 py-8 bg-[#09080E] sticky top-0 z-40">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => {
                setSelectedCategory(cat.id);
                if (searched || selectedCity) searchEvents({ category: cat.id !== 'all' ? cat.id : null });
              }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap backdrop-blur-xl ${
                selectedCategory === cat.id
                  ? 'text-white border border-white/20 shadow-[0_0_25px_rgba(255,0,127,0.3)]'
                  : 'bg-white/[0.05] text-white/60 border border-white/[0.08] hover:bg-white/[0.1] hover:text-white'
              }`}
              style={selectedCategory === cat.id ? { backgroundColor: `${cat.color}30`, borderColor: `${cat.color}50` } : {}}
              data-testid={`tab-${cat.id}`}
            >
              <cat.icon className="w-4 h-4" style={selectedCategory === cat.id ? { color: cat.color } : {}} />
              {cat.name}
            </button>
          ))}
        </div>
      </section>

      {/* ==================== SEARCH RESULTS ==================== */}
      <AnimatePresence>
        {searched && (
          <motion.section 
            initial={{ opacity: 0, y: 50 }} 
            animate={{ opacity: 1, y: 0 }} 
            exit={{ opacity: 0 }} 
            className="px-4 md:px-16 py-16 bg-[#09080E]"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-[#FF007F] animate-spin mb-4" />
                <p className="text-white/60">Discovering experiences...</p>
              </div>
            ) : events.length > 0 ? (
              <>
                <SectionHeader 
                  eyebrow={selectedCity || 'Search Results'} 
                  title={`${events.length} Experiences Found`} 
                  accentColor="#FF007F" 
                />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {events.map((event, index) => (
                    <EventCard key={event.id} event={event} size={index === 0 ? 'large' : 'normal'} />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Globe className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h3 className="text-white text-xl font-semibold mb-2">No Experiences Found</h3>
                <p className="text-white/50">Try a different destination or category</p>
              </div>
            )}
          </motion.section>
        )}
      </AnimatePresence>

      {/* ==================== TRENDING EXPERIENCES ==================== */}
      {!searched && trendingEvents.length > 0 && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader 
            eyebrow="Hot Right Now" 
            title="Trending Experiences" 
            subtitle="The most sought-after events and experiences happening around the world"
            accentColor="#FF007F"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trendingEvents.slice(0, 8).map((event, index) => (
              <EventCard key={event.id} event={event} size={index === 0 ? 'large' : 'normal'} />
            ))}
          </div>
        </section>
      )}

      {/* ==================== THIS WEEKEND ==================== */}
      {!searched && weekendEvents.length > 0 && (
        <section className="px-4 md:px-16 py-16 bg-[#0D0B14]">
          <SectionHeader 
            eyebrow="Don't Miss Out" 
            title="This Weekend" 
            subtitle="Last-minute plans? These experiences are happening in the next few days"
            accentColor="#00E5FF"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {weekendEvents.slice(0, 6).map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}

      {/* ==================== EDITORIAL CATEGORIES ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-16 bg-[#09080E]">
          <SectionHeader 
            eyebrow="Explore By Interest" 
            title="Find Your Perfect Experience" 
            accentColor="#FFD700"
          />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CATEGORIES.filter(c => c.id !== 'all').map((cat) => (
              <motion.button
                key={cat.id}
                onClick={() => {
                  setSelectedCategory(cat.id);
                  searchEvents({ category: cat.id });
                }}
                whileHover={{ y: -5 }}
                className="relative overflow-hidden rounded-2xl aspect-square group"
              >
                <div 
                  className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity"
                  style={{ backgroundColor: cat.color }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${cat.color}30` }}
                  >
                    <cat.icon className="w-8 h-8" style={{ color: cat.color }} />
                  </div>
                  <span className="text-white font-semibold text-lg">{cat.name}</span>
                  <span className="text-white/60 text-sm">{cat.emoji}</span>
                </div>
              </motion.button>
            ))}
          </div>
        </section>
      )}

      {/* ==================== INSPIRATION QUOTE ==================== */}
      {!searched && (
        <section className="px-4 md:px-16 py-32 bg-[#0D0B14] text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Sparkles className="w-12 h-12 text-[#FF007F] mx-auto mb-8" />
            <blockquote className="font-['Playfair_Display'] text-3xl md:text-5xl text-white font-bold max-w-4xl mx-auto leading-tight mb-8">
              "Life is either a daring adventure or nothing at all."
            </blockquote>
            <p className="text-white/50 uppercase tracking-widest">— Helen Keller</p>
          </motion.div>
        </section>
      )}

      {/* AI Experience Concierge */}
      <ExperienceConcierge 
        onSearch={handleConciergeSearch}
        currentCity={selectedCity}
      />
    </div>
  );
}
